// src/pages/admin/Dashboard.tsx
// Admin dashboard: RSVP totals, attendee list (paginated), photo moderation,
// golf roster.
//
// Schema migration — May 2026:
//   New `attendees: Attendee[]` field is the per-person source of truth.
//   For golfer counts and names, prefer attendees[].golfing; fall back to
//   the legacy `golfPlaying` boolean for unmigrated docs.

import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../auth/useAuth";
import { Link } from "react-router-dom";
import PhotoModeration from "../../components/admin/PhotoModeration";
import AddRsvpModal from "../../components/admin/AddRsvpModal";

interface Attendee {
  name: string;
  type: "adult" | "child";
  tshirtSize?: string;
  golfing?: boolean;
}

interface Totals {
  total: number;
  headcount: number;
  adults?: number;
  children?: number;
  employees: number;
  guests: number;
  verified: number;
  golfers?: number;
  declined?: number;
}
interface RsvpRow {
  id: string;
  source: "employee" | "guest";
  isEmployee?: boolean;
  name?: string;
  email?: string;
  upn?: string;
  displayName?: string;
  attending?: boolean;
  partySize: number;
  adultsCount?: number;
  childrenCount?: number;
  guestNames?: string[];
  tshirtSizes?: string[] | { adult?: string[]; youth?: string[] };
  golfPlaying?: boolean;
  attendees?: Attendee[];
  dietaryNotes?: string;
  enteredByUpn?: string;
  verifiedAt?: string;
  updatedAt: string;
}
interface EventFlags {
  golfEnabled?: boolean;
  tshirtsEnabled?: boolean;
}

type SourceFilter = "all" | "employee" | "guest" | "declined";

interface GolferRow {
  name: string;
  party: string;
  email: string;
  source: "employee" | "guest";
  isLegacy: boolean;
}

type ActiveTab = "rsvp" | "photos" | "golfers";

function fmt(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function nameOf(row: RsvpRow): string {
  return row.displayName ?? row.name ?? emailOf(row);
}
function emailOf(row: RsvpRow): string {
  return row.email ?? row.upn ?? "—";
}
/** True if this RSVP belongs to a WIC employee — either signed in via SSO
 *  (source 'employee') or self-identified on the public/manual form
 *  (isEmployee). The Type column and employee/guest totals classify on this,
 *  not on `source` alone, so a staff member who RSVP'd via the public link
 *  still reads as an employee (issue #17). */
function isEmployeeRow(row: RsvpRow): boolean {
  return row.isEmployee === true || row.source === "employee";
}
function adultsOf(row: RsvpRow): number {
  if (typeof row.adultsCount === "number") return row.adultsCount;
  if (typeof row.childrenCount === "number") return Math.max(0, (row.partySize ?? 0) - row.childrenCount);
  return row.partySize ?? 0;
}
function childrenOf(row: RsvpRow): number {
  return typeof row.childrenCount === "number" ? row.childrenCount : 0;
}

/** Count of golfers in this RSVP. Prefers attendees[]; falls back to the
 *  legacy `golfPlaying` bool (which counts as 1 if true, 0 otherwise). */
function golfersOf(row: RsvpRow): number {
  if (Array.isArray(row.attendees)) {
    return row.attendees.filter(a => a?.golfing === true).length;
  }
  return row.golfPlaying === true ? 1 : 0;
}

/** Names of golfers in this RSVP. Returns empty array for legacy docs that
 *  only have `golfPlaying: true` — for those the cell falls back to ⛳. */
function golferNamesOf(row: RsvpRow): string[] {
  if (Array.isArray(row.attendees)) {
    return row.attendees
      .filter(a => a?.golfing === true)
      .map(a => a.name?.trim() || "—");
  }
  return [];
}

// Helper: use server total if present, otherwise fall back to in-memory count
function statValue(serverVal: number | undefined, fallback: number): string | number {
  if (typeof serverVal === "number") return serverVal || "—";
  return fallback || "—";
}

// ─── CSV export (issue #8) ───────────────────────────────────────────────────

function csvEscape(v: unknown): string {
  const s = v === undefined || v === null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Attendee names — prefers attendees[]; falls back to legacy guestNames. */
function attendeeNamesOf(row: RsvpRow): string {
  if (Array.isArray(row.attendees)) {
    return row.attendees
      .map((a, i) => a.name?.trim() || `${a.type === "child" ? "Child" : "Adult"} ${i + 1}`)
      .join("; ");
  }
  return (row.guestNames ?? []).join("; ");
}

/** T-shirt sizes — "name: size" pairs from attendees[]; legacy arrays otherwise. */
function tshirtSummaryOf(row: RsvpRow): string {
  if (Array.isArray(row.attendees)) {
    return row.attendees
      .filter(a => a.tshirtSize)
      .map(a => `${a.name?.trim() || a.type}: ${a.tshirtSize}`)
      .join("; ");
  }
  const ts = row.tshirtSizes;
  if (Array.isArray(ts)) return ts.join("; ");
  if (ts && typeof ts === "object") {
    return [...(ts.adult ?? []), ...(ts.youth ?? [])].join("; ");
  }
  return "";
}

function buildCsv(rows: RsvpRow[]): string {
  const header = [
    "Name", "Email", "Type", "Attending", "Adults", "Children", "Party Size",
    "Attendees", "T-Shirts", "Golfers", "Dietary Notes", "Verified",
    "Entered By Admin", "Updated",
  ];
  const lines = rows.map(row => [
    nameOf(row),
    emailOf(row),
    isEmployeeRow(row) ? "employee" : "guest",
    row.attending === false ? "No" : "Yes",
    adultsOf(row),
    childrenOf(row),
    row.partySize ?? 0,
    attendeeNamesOf(row),
    tshirtSummaryOf(row),
    golferNamesOf(row).join("; ") || (golfersOf(row) > 0 ? String(golfersOf(row)) : ""),
    row.dietaryNotes ?? "",
    row.verifiedAt ? fmt(row.verifiedAt) : "",
    row.enteredByUpn ?? "",
    fmt(row.updatedAt),
  ].map(csvEscape).join(","));
  return [header.join(","), ...lines].join("\r\n");
}

/** Golf roster export (issue #32) — mirrors the on-screen roster columns. */
function buildGolfersCsv(rows: GolferRow[]): string {
  const header = ["Name", "Party", "Email", "Source", "Legacy"];
  const lines = rows.map(g => [
    g.name,
    g.party === g.name ? "" : g.party,
    g.email ?? "",
    g.source,
    g.isLegacy ? "yes" : "",
  ].map(csvEscape).join(","));
  return [header.join(","), ...lines].join("\r\n");
}

export default function Dashboard({ initialTab = "rsvp" }: { initialTab?: ActiveTab }) {
  const { getApiToken, roles } = useAuth();
  const isAdmin = roles.includes("WICnic.Admin");

  const [totals, setTotals]     = useState<Totals | null>(null);
  const [rsvps, setRsvps]       = useState<RsvpRow[]>([]);
  const [rsvpPage, setRsvpPage] = useState(0);
  const [hasMore, setHasMore]   = useState(false);
  const [source, setSource]     = useState<SourceFilter>("all");
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [deletingId, setDeletingId]   = useState<string | null>(null);
  const [tab, setTab] = useState<ActiveTab>(initialTab);
  const [golfEnabled, setGolfEnabled] = useState<boolean>(false);
  const [tshirtsEnabled, setTshirtsEnabled] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editRow, setEditRow] = useState<RsvpRow | null>(null);
  const [exporting, setExporting] = useState(false);

  // Golf roster state — fetched lazily when the Golfers tab is opened
  const [golfers, setGolfers]               = useState<GolferRow[]>([]);
  const [golfersLoading, setGolfersLoading] = useState(false);
  const [golfersFetched, setGolfersFetched] = useState(false);

  // In-memory fallbacks (used when server totals not available — old API)
  const loadedAdults   = rsvps.reduce((sum, r) => sum + adultsOf(r),   0);
  const loadedChildren = rsvps.reduce((sum, r) => sum + childrenOf(r), 0);
  const loadedGolfers  = rsvps.reduce((sum, r) => sum + golfersOf(r),  0);

  useEffect(() => {
    fetch("/api/public/event?year=2026")
      .then(r => r.ok ? r.json() : null)
      .then((d: EventFlags | null) => {
        if (d) {
          setGolfEnabled(d.golfEnabled === true);
          setTshirtsEnabled(d.tshirtsEnabled === true);
        }
      })
      .catch(() => { });
  }, []);

  const fetchRsvps = useCallback(async (page: number, src: SourceFilter) => {
    setRsvpLoading(true);
    try {
      const token = await getApiToken();
      // "declined" is an attending-state filter, not a source — map it to
      // source=all&attending=false (issue #9).
      const params = src === "declined"
        ? "source=all&attending=false"
        : `source=${src}`;
      const res = await fetch(
        `/api/manage/rsvp?year=2026&${params}&page=${page}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setRsvps(page === 0 ? (data.rsvps ?? []) : (prev) => [...prev, ...(data.rsvps ?? [])]);
      setTotals(data.totals ?? { total: 0, headcount: 0, employees: 0, guests: 0, verified: 0 });
      setHasMore(data.hasMore);
    } finally { setRsvpLoading(false); }
  }, [getApiToken]);

  useEffect(() => {
    setRsvpPage(0);
    fetchRsvps(0, source);
  }, [source, fetchRsvps]);

  const fetchGolfers = useCallback(async () => {
    setGolfersLoading(true);
    try {
      const token = await getApiToken();
      const res = await fetch("/api/manage/golfers?year=2026", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setGolfers([]);
        return;
      }
      const data = await res.json();
      setGolfers(data.golfers ?? []);
    } catch {
      setGolfers([]);
    } finally {
      setGolfersLoading(false);
      setGolfersFetched(true);
    }
  }, [getApiToken]);

  // Lazy-load the roster when the tab is opened (re-fetches on every tab open
  // so admin sees fresh data after RSVPs come in — cheap query, no harm).
  useEffect(() => {
    if (tab === "golfers" && golfEnabled) {
      fetchGolfers();
    }
  }, [tab, golfEnabled, fetchGolfers]);

  async function handleDeleteRsvp(row: RsvpRow) {
    const who = nameOf(row);
    if (!confirm(`Delete RSVP for ${who}?\n\nThis is permanent.`)) return;

    setDeletingId(row.id);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/rsvp/${encodeURIComponent(row.id)}?year=2026`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 204) {
        setRsvps(prev => prev.filter(r => r.id !== row.id));
        fetchRsvps(0, source);
      } else {
        const body = await res.json().catch(() => ({}));
        alert(`Delete failed (${res.status})${body?.message ? ': ' + body.message : ''}`);
      }
    } catch (err) {
      alert("Delete failed. Try again.");
    } finally {
      setDeletingId(null);
    }
  }

  // Export everything — attending AND declined — so the full picture is
  // captured (issue #8). Pages through the manage endpoint client-side.
  async function handleExportCsv() {
    setExporting(true);
    try {
      const token = await getApiToken();
      const all: RsvpRow[] = [];
      for (const attending of ["true", "false"]) {
        for (let page = 0; ; page++) {
          const res = await fetch(
            `/api/manage/rsvp?year=2026&source=all&attending=${attending}&page=${page}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!res.ok) throw new Error(`fetch failed (${res.status})`);
          const data = await res.json();
          all.push(...(data.rsvps ?? []));
          if (!data.hasMore) break;
        }
      }
      // BOM so Excel opens UTF-8 correctly
      const blob = new Blob([String.fromCharCode(0xfeff) + buildCsv(all)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "wicnic-rsvps-2026.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Try again.");
    } finally {
      setExporting(false);
    }
  }

  // Golf roster export (issue #32). Roster is already loaded in state, so this
  // builds the file from memory — no extra fetch needed.
  function handleExportGolfers() {
    if (golfers.length === 0) return;
    // BOM so Excel opens UTF-8 correctly
    const blob = new Blob([String.fromCharCode(0xfeff) + buildGolfersCsv(golfers)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "wicnic-golf-roster-2026.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <style>{`
        .admin-page { min-height: 100vh; background: var(--bg); padding-bottom: 5rem; }
        .admin-header { background: var(--black); color: var(--white); padding: 3rem 4rem 2.5rem; border-bottom: 3px solid var(--red); }
        .admin-header-inner { max-width: 1200px; margin: 0 auto; }
        .eyebrow { font-family: var(--font-display); font-size: 0.72rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); margin-bottom: 0.5rem; }
        .admin-header h1 { font-family: var(--font-display); font-size: clamp(1.6rem, 3.5vw, 2.4rem); font-weight: 700; text-transform: uppercase; margin: 0; }
        .admin-role-badge { display: inline-block; font-family: var(--font-display); font-size: 0.65rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; padding: 0.25rem 0.65rem; margin-top: 0.75rem; background: ${isAdmin ? "var(--red)" : "#2C2C2C"}; color: var(--white); }
        .stats-wrap { max-width: 1200px; margin: 0 auto; padding: 2.5rem 4rem 0; }
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 1px; background: var(--border); border: 1px solid var(--border); }
        .stat-card { background: var(--white); padding: 1.5rem 1.75rem; display: flex; flex-direction: column; gap: 0.25rem; }
        .stat-label { font-family: var(--font-display); font-size: 0.65rem; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); }
        .stat-value { font-family: var(--font-display); font-size: 2rem; font-weight: 700; color: var(--black); line-height: 1; }
        .stat-value.red { color: var(--red); }
        .tabs-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 4rem 0; }
        .tabs { display: flex; gap: 0; border-bottom: 2px solid var(--border); }
        .tab-btn { font-family: var(--font-display); font-size: 0.8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; padding: 0.75rem 1.5rem; border: none; background: none; color: var(--muted); cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.15s; }
        .tab-btn.active { color: var(--red); border-bottom-color: var(--red); }
        .tab-btn:hover:not(.active) { color: var(--steel); }
        .tab-content { max-width: 1200px; margin: 0 auto; padding: 2rem 4rem; }
        @media (max-width: 768px) {
          .stats-wrap, .tabs-wrap, .tab-content { padding-left: 1.5rem; padding-right: 1.5rem; }
          .admin-header { padding: 2.5rem 1.5rem 2rem; }
        }
        .filter-bar { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
        .filter-label { font-family: var(--font-display); font-size: 0.7rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.15em; color: var(--muted); margin-right: 0.25rem; }
        .filter-chip { font-family: var(--font-display); font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; padding: 0.35rem 0.85rem; border: 1px solid var(--border); background: var(--white); color: var(--mid); cursor: pointer; transition: all 0.15s; }
        .filter-chip.active { background: var(--black); border-color: var(--black); color: var(--white); }
        .filter-chip:hover:not(.active) { border-color: var(--mid); }
        .rsvp-table-wrap { overflow-x: auto; }
        table { width: 100%; border-collapse: collapse; font-family: var(--font-body); font-size: 0.88rem; }
        thead tr { background: var(--black); }
        thead th { font-family: var(--font-display); font-size: 0.65rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.15em; color: rgba(255,255,255,0.65); text-align: left; padding: 0.75rem 1rem; white-space: nowrap; }
        tbody tr { border-bottom: 1px solid var(--border); }
        tbody tr:hover { background: var(--bg); }
        tbody td { padding: 0.7rem 1rem; color: var(--steel); vertical-align: middle; }
        .name-cell { font-weight: 500; color: var(--black); }
        .email-cell { color: var(--steel); font-family: var(--font-body); }
        .source-chip { font-family: var(--font-display); font-size: 0.6rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; padding: 0.2rem 0.5rem; }
        .chip-employee { background: #e3f2fd; color: #1565c0; }
        .chip-guest    { background: #f3e5f5; color: #6a1b9a; }
        .verified-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #4caf50; margin-right: 0.4rem; }
        .unverified-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: var(--border); margin-right: 0.4rem; }
        .golf-cell { display: inline-flex; align-items: center; gap: 0.35rem; }
        .golf-tag { font-size: 1.05rem; line-height: 1; }
        .golf-count { font-family: var(--font-display); font-size: 0.78rem; font-weight: 700; color: var(--black); }
        .row-action-btn { font-family: var(--font-display); font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; padding: 0.35rem 0.75rem; background: transparent; border: 1px solid var(--border); color: var(--red); cursor: pointer; transition: all 0.12s; }
        .row-action-btn:hover:not(:disabled) { background: var(--red); color: var(--white); border-color: var(--red); }
        .row-action-btn:disabled { opacity: 0.5; cursor: wait; }
        .load-more-btn { display: block; margin: 1.5rem auto 0; font-family: var(--font-display); font-size: 0.78rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; padding: 0.65rem 2rem; border: 1px solid var(--border); background: var(--white); color: var(--mid); cursor: pointer; transition: all 0.15s; }
        .load-more-btn:hover { background: var(--black); color: var(--white); border-color: var(--black); }
        .action-btn { font-family: var(--font-display); font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--white); background: var(--red); border: none; padding: 0.65rem 1.5rem; cursor: pointer; transition: background 0.15s; text-decoration: none; display: inline-block; }
        .action-btn:hover:not(:disabled) { background: var(--red-dark); }
        .action-btn:disabled { background: var(--muted); cursor: not-allowed; }
        .state-box { padding: 4rem 2rem; text-align: center; font-family: var(--font-body); color: var(--muted); }

        /* Golf Roster */
        .roster-toolbar {
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 1rem; flex-wrap: wrap;
          margin-bottom: 1.25rem;
        }
        .roster-title {
          font-family: var(--font-display); font-weight: 700;
          font-size: 1.1rem; text-transform: uppercase; letter-spacing: 0.08em;
          color: var(--black); margin: 0;
        }
        .roster-meta {
          font-family: var(--font-body); font-size: 0.85rem; color: var(--muted);
        }
        .roster-actions { display: flex; gap: 0.6rem; flex-wrap: wrap; }
        .roster-print-btn {
          font-family: var(--font-display); font-size: 0.72rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.1em;
          padding: 0.45rem 1rem; background: var(--white); color: var(--black);
          border: 1px solid var(--border); cursor: pointer;
          transition: all 0.15s;
        }
        .roster-print-btn:hover:not(:disabled) { background: var(--black); color: var(--white); border-color: var(--black); }
        .roster-print-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .legacy-badge {
          font-family: var(--font-display); font-size: 0.6rem; font-weight: 600;
          letter-spacing: 0.08em; text-transform: uppercase;
          padding: 0.15rem 0.45rem; margin-left: 0.5rem;
          background: #fff8e1; color: #f57f17;
        }
        .roster-print-header {
          display: none;  /* only shown in print */
        }

        @media print {
          /* Hide chrome */
          .admin-header, .stats-wrap, .tabs-wrap, .roster-toolbar,
          .row-action-btn, .load-more-btn { display: none !important; }
          .admin-page { background: white; padding: 0; }
          .tab-content { padding: 0.5in; max-width: none; }
          /* Show print-only header */
          .roster-print-header { display: block; margin-bottom: 1rem; }
          .roster-print-header h1 {
            font-family: var(--font-display); font-weight: 700;
            text-transform: uppercase; margin: 0 0 0.25rem;
            font-size: 1.6rem;
          }
          .roster-print-header p {
            font-family: var(--font-body); font-size: 0.95rem;
            margin: 0; color: #555;
          }
          /* Print table — slightly wider, bigger font for clipboarding */
          table { font-size: 0.95rem; }
          thead tr { background: white !important; }
          thead th { color: black !important; border-bottom: 2px solid black; padding: 0.6rem 0.4rem; }
          tbody tr { border-bottom: 1px solid #ddd; }
          tbody td { padding: 0.55rem 0.4rem; color: black; }
          .source-chip { background: transparent !important; color: black !important; padding: 0; }
          .legacy-badge { background: transparent !important; color: #888 !important; padding: 0; }
        }
      `}</style>

      <div className="admin-page">
        <div className="admin-header">
          <div className="admin-header-inner">
            <p className="eyebrow">— — Admin Dashboard</p>
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem", flexWrap: "wrap", alignItems: "center" }}>
              <Link to="/admin/event" className="action-btn">Edit Event →</Link>
              {isAdmin && <Link to="/admin/raffle" className="action-btn">Raffle →</Link>}
              <span className="admin-role-badge">{isAdmin ? "Admin" : "Editor"}</span>
            </div>
          </div>
        </div>

        <div className="stats-wrap">
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">RSVPs</span>
              <span className="stat-value red">{totals?.total ?? "—"}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Headcount</span>
              <span className="stat-value">{totals?.headcount ?? "—"}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Adults</span>
              <span className="stat-value">{statValue(totals?.adults, loadedAdults)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Children</span>
              <span className="stat-value">{statValue(totals?.children, loadedChildren)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Employees</span>
              <span className="stat-value">{totals?.employees ?? "—"}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Guests</span>
              <span className="stat-value">{totals?.guests ?? "—"}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Verified</span>
              <span className="stat-value">{totals?.verified ?? "—"}</span>
            </div>
            {golfEnabled && (
              <div className="stat-card">
                <span className="stat-label">⛳ Golfers</span>
                <span className="stat-value">{statValue(totals?.golfers, loadedGolfers)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="tabs-wrap">
          <div className="tabs">
            <button className={`tab-btn${tab === "rsvp" ? " active" : ""}`} onClick={() => setTab("rsvp")}>Attendees</button>
            <button className={`tab-btn${tab === "photos" ? " active" : ""}`} onClick={() => setTab("photos")}>Photos</button>
            {golfEnabled && (
              <button className={`tab-btn${tab === "golfers" ? " active" : ""}`} onClick={() => setTab("golfers")}>Golf Roster</button>
            )}
          </div>
        </div>

        {tab === "rsvp" && (
          <div className="tab-content">
            <div className="filter-bar">
              <span className="filter-label">Show:</span>
              {(["all", "employee", "guest", "declined"] as const).map((s) => (
                <button key={s} className={`filter-chip${source === s ? " active" : ""}`} onClick={() => setSource(s)}>
                  {s}
                </button>
              ))}
              <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
                <button className="filter-chip" onClick={handleExportCsv} disabled={exporting} title="Download all RSVPs (attending + declined) as CSV">
                  {exporting ? "Exporting…" : "⬇ Export CSV"}
                </button>
                <button className="filter-chip" style={{ background: "var(--red)", borderColor: "var(--red)", color: "var(--white)" }} onClick={() => setShowAddModal(true)}>
                  + Add RSVP
                </button>
              </div>
            </div>

            {rsvpLoading && rsvps.length === 0 ? (
              <div className="state-box"><p>Loading attendees…</p></div>
            ) : rsvps.length === 0 ? (
              <div className="state-box"><p>No RSVPs yet.</p></div>
            ) : (
              <div className="rsvp-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th style={{ textAlign: "right" }}>Adults</th>
                      <th style={{ textAlign: "right" }}>Children</th>
                      {golfEnabled && <th style={{ textAlign: "center" }}>Golf</th>}
                      <th>Verified</th>
                      <th>Updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rsvps.map((row) => {
                      const golfCount = golfersOf(row);
                      const golfNames = golferNamesOf(row);
                      // Tooltip lists golfer names from attendees[] when available;
                      // legacy docs (golfPlaying:true only) fall back to ⛳ with no
                      // names — admin can hover/click row for full doc if needed.
                      const golfTitle = golfNames.length > 0
                        ? `Golfers: ${golfNames.join(", ")}`
                        : (golfCount > 0 ? "Playing in tournament (legacy record)" : "");
                      return (
                        <tr key={row.id}>
                          <td className="name-cell">{nameOf(row)}</td>
                          <td className="email-cell">{emailOf(row)}</td>
                          <td>
                            {(() => {
                              const emp = isEmployeeRow(row);
                              return <span className={`source-chip chip-${emp ? "employee" : "guest"}`}>{emp ? "employee" : "guest"}</span>;
                            })()}
                          </td>
                          <td style={{ textAlign: "right" }}>{adultsOf(row)}</td>
                          <td style={{ textAlign: "right" }}>{childrenOf(row)}</td>
                          {golfEnabled && (
                            <td style={{ textAlign: "center" }}>
                              {golfCount > 0 ? (
                                <span className="golf-cell" title={golfTitle}>
                                  <span className="golf-tag">⛳</span>
                                  {golfCount > 1 && <span className="golf-count">×{golfCount}</span>}
                                </span>
                              ) : (
                                <span style={{ color: "var(--muted)" }}>—</span>
                              )}
                            </td>
                          )}
                          <td>
                            {row.verifiedAt ? (
                              <><span className="verified-dot" />{fmt(row.verifiedAt)}</>
                            ) : (
                              <><span className="unverified-dot" />Pending</>
                            )}
                          </td>
                          <td style={{ color: "var(--muted)", fontSize: "0.82rem" }}>{fmt(row.updatedAt)}</td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <button
                              className="row-action-btn"
                              onClick={() => setEditRow(row)}
                              title="Edit this RSVP"
                            >
                              Edit
                            </button>
                            {isAdmin && (
                              <button
                                className="row-action-btn"
                                style={{ marginLeft: "0.4rem" }}
                                onClick={() => handleDeleteRsvp(row)}
                                disabled={deletingId === row.id}
                                title="Delete this RSVP"
                              >
                                {deletingId === row.id ? "…" : "Delete"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {hasMore && (
                  <button
                    className="load-more-btn"
                    disabled={rsvpLoading}
                    onClick={() => {
                      const next = rsvpPage + 1;
                      setRsvpPage(next);
                      fetchRsvps(next, source);
                    }}
                  >
                    {rsvpLoading ? "Loading…" : "Load more"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "photos" && (
          <div className="tab-content">
            <PhotoModeration />
          </div>
        )}

        {tab === "golfers" && golfEnabled && (
          <div className="tab-content">
            {/* Print-only header — appears at top of printout */}
            <div className="roster-print-header">
              <h1>WICnic 2026 — Golf Roster</h1>
              <p>Riverdale Dunes · 8:30 AM Shotgun · {golfers.length} golfer{golfers.length === 1 ? "" : "s"}</p>
            </div>

            <div className="roster-toolbar">
              <div>
                <p className="roster-title">Golf Roster</p>
                <p className="roster-meta">
                  {golfersLoading
                    ? "Loading…"
                    : `${golfers.length} golfer${golfers.length === 1 ? "" : "s"} signed up · sorted by name`}
                </p>
              </div>
              <div className="roster-actions">
                <button
                  className="roster-print-btn"
                  onClick={handleExportGolfers}
                  disabled={golfers.length === 0}
                  title="Download the roster as a spreadsheet (opens in Excel)"
                >
                  ⬇ Export to Excel
                </button>
                <button className="roster-print-btn" onClick={() => window.print()}>
                  Print Roster
                </button>
              </div>
            </div>

            {golfersLoading && golfers.length === 0 ? (
              <div className="state-box"><p>Loading roster…</p></div>
            ) : golfersFetched && golfers.length === 0 ? (
              <div className="state-box"><p>No golfers signed up yet.</p></div>
            ) : (
              <div className="rsvp-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Party</th>
                      <th>Email</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {golfers.map((g, i) => (
                      <tr key={`${g.email}-${g.name}-${i}`}>
                        <td className="name-cell">
                          {g.name}
                          {g.isLegacy && (
                            <span
                              className="legacy-badge"
                              title="From a pre-update RSVP — golfer attribution is best-effort"
                            >
                              legacy
                            </span>
                          )}
                        </td>
                        <td>{g.party === g.name ? "—" : g.party}</td>
                        <td className="email-cell">{g.email || "—"}</td>
                        <td>
                          <span className={`source-chip chip-${g.source}`}>{g.source}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddRsvpModal
          golfEnabled={golfEnabled}
          tshirtsEnabled={tshirtsEnabled}
          getApiToken={getApiToken}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setRsvpPage(0); fetchRsvps(0, source); }}
        />
      )}

      {editRow && (
        <AddRsvpModal
          existing={editRow}
          golfEnabled={golfEnabled}
          tshirtsEnabled={tshirtsEnabled}
          getApiToken={getApiToken}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); setRsvpPage(0); fetchRsvps(0, source); }}
        />
      )}
    </>
  );
}
